import {
  type Patient,
  useRxCollectionPatient,
} from "$global-providers/rxdb-provider/collections/patient";
import {
  type ApolloClient,
  gql,
  type QueryResult,
  useApolloClient,
} from "@apollo/client";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import type { RxCollection, RxDocument } from "rxdb";
import { mapGqlSyncablePatientToRxPatient } from "../replication/patient";
import {
  GetPatientFallbackHookDocument,
  type GetPatientFallbackHookQuery,
  type GetPatientFallbackHookQueryVariables,
} from "./__generated__/patient.gql";

gql`
  query GetPatientFallbackHook($input: GetPatientInput!) {
    getPatient(input: $input) {
      patient {
        __typename
        ...WholeSyncablePatient
      }
    }
  }
`;

type Input = {
  id: string;
};

type Result = {
  loading: boolean;
  error?: FallbackError;
  data?: RxDocument<Patient>;
};

const getPatientFromGqlAndInsertIntoRxDB = async ({
  input,
  collection,
  query,
}: {
  input?: Input;
  collection: RxCollection<Patient>;
  query: (
    input: Input,
  ) => Promise<
    Pick<
      QueryResult<
        GetPatientFallbackHookQuery,
        GetPatientFallbackHookQueryVariables
      >,
      "data" | "error"
    >
  >;
}): Promise<Pick<Result, "data" | "error">> => {
  try {
    const gqlResult = await query(input);
    const gqlPatient = gqlResult.data?.getPatient.patient;
    if (gqlResult.error) {
      console.error(gqlResult.error);
      return {
        error: FallbackError.FAILED_TO_FETCH,
      };
    }
    if (!gqlPatient) {
      console.debug("Patient not found on server.");
      return { error: FallbackError.NOT_FOUND_ON_SERVER };
    }

    const data = await collection.upsert(
      mapGqlSyncablePatientToRxPatient(gqlPatient),
    );

    return { data };
  } catch (e) {
    console.error(e);
    return {
      error: FallbackError.FAILED_TO_FETCH,
    };
  }
};

const getPatientData =
  ({
    setError,
    setLoading,
    setData,
    collection,
    apolloClient,
  }: {
    setError: React.Dispatch<React.SetStateAction<FallbackError | undefined>>;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setData: React.Dispatch<
      React.SetStateAction<RxDocument<Patient> | undefined>
    >;
    collection: RxCollection<Patient>;
    apolloClient: ApolloClient<object>;
  }) =>
  async (input: Input) => {
    setError(undefined);
    setLoading(true);
    let newError: FallbackError | undefined;
    let newData: RxDocument<Patient> | undefined;

    try {
      const patient = await collection
        .findOne({
          selector: { id: input.id },
        })
        .exec();
      if (patient) {
        setData(createUpdateProxy(patient, setData));
        setLoading(false);
        return { loading: false, error: undefined, data: patient };
      }

      const result = await getPatientFromGqlAndInsertIntoRxDB({
        input,
        collection,
        query: (input) =>
          apolloClient.query<
            GetPatientFallbackHookQuery,
            GetPatientFallbackHookQueryVariables
          >({ query: GetPatientFallbackHookDocument, variables: { input } }),
      });
      if (result.error) {
        newError = result.error;
        setError(result.error);
      }
      if (result.data) {
        newData = result.data;
        setData(createUpdateProxy(result.data, setData));
      }
    } catch (e) {
      console.error(e);
      newError = FallbackError.FAILED_TO_FETCH;
      setError(FallbackError.FAILED_TO_FETCH);
    }
    setLoading(false);

    return { loading: false, error: newError, data: newData };
  };
export enum FallbackError {
  NOT_FOUND_ON_SERVER = "NOT_FOUND_ON_SERVER",
  FAILED_TO_FETCH = "FAILED_TO_FETCH",
}

/**
 * @deprecated These will be replaced by the incoming pull sync pattern:
 * https://elephant.slab.com/posts/just-in-time-sync-pattern-wip-grv582xm
 *
 * Fetches a patient from RxDB, and if not found falls back to the server while
 * saving this patient to RxDB so future queries will find the patient.
 *
 * Note: Currently, if you desire live updates to the UI then you must use
 * the methods on the returned Patient object to perform the updates, i.e.
 * `incrementalPatch` or `update`. In the future this hook could be improved
 * to also listen to updates made to the patient outside of the hook.
 *
 * @param input Supply any of the optional ids to find a single patient.
 * @returns The patient data, loading state, and error state.
 */
export const usePatient = (input: Input): Result => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<FallbackError | undefined>(undefined);
  const [data, setData] = useState<RxDocument<Patient> | undefined>(undefined);

  const collection = useRxCollectionPatient();
  const apolloClient = useApolloClient();

  useEffect(() => {
    if (data || !loading || !collection || !apolloClient) {
      return;
    }
    getPatientData({
      setError,
      setLoading,
      setData,
      collection,
      apolloClient,
    })(input);
  }, [data, loading, collection, apolloClient, input]);

  return {
    loading,
    error,
    data,
  };
};

/**
 * @deprecated These will be replaced by the incoming pull sync pattern:
 * https://elephant.slab.com/posts/just-in-time-sync-pattern-wip-grv582xm
 *
 * See `usePatient` for more details. This provides a lazy version of the hook.
 *
 * @returns A function to fetch a patient, and the loading state and error state.
 */
export const useLazyPatient = (): [
  (input: Input) => Promise<Result>,
  Result,
] => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<FallbackError | undefined>(undefined);
  const [data, setData] = useState<RxDocument<Patient> | undefined>(undefined);

  const collection = useRxCollectionPatient();
  const apolloClient = useApolloClient();

  /* biome-ignore lint/correctness/useExhaustiveDependencies: collection and
   * apolloClient are not guaranteed stable dependencies.
   */
  const getPatient = useCallback<(input: Input) => Promise<Result>>(
    collection
      ? getPatientData({
          setError,
          setLoading,
          setData,
          collection,
          apolloClient,
        })
      : async (_: Input): Promise<Result> => {
          // Hopefully we don't see this in production. If we do then we need to
          // make getPatient nullable in the return type and ensure all callers
          // are checking for it before calling.
          console.warn(
            "useLazyPatient called before collection is ready. Fix comment in code if this log is seen.",
          );
          return { loading: true };
        },
    [collection, apolloClient],
  );

  return [getPatient, { loading: loading, error, data }] as const;
};

/**
 * Wraps an RxDocument in a Proxy such that any method calls that successfully
 * update the document trigger a follow-on action.
 *
 * In this scenario we use it for live-updating the UI when the document is
 * updated by calling the setData of the hook.
 *
 * @param data Object to have it's updates trigger setData.
 * @param setData Function to call when the object is updated.
 */
function createUpdateProxy<T>(
  data: RxDocument<T>,
  setData: Dispatch<SetStateAction<RxDocument<T>>>,
): RxDocument<T> {
  return new Proxy(data, {
    get: (target, prop, receiver) => {
      const originalValue = Reflect.get(target, prop, receiver);
      if (
        typeof originalValue === "function" &&
        typeof prop === "string" &&
        (
          [
            "update",
            "modify",
            "patch",
            "remove",
            "incrementalUpdate",
            "incrementalModify",
            "incrementalPatch",
            "incrementalRemove",
          ] as (keyof RxDocument)[]
        ).includes(prop as keyof RxDocument)
      ) {
        return function (...args: unknown[]) {
          return originalValue
            .apply(target, args)
            .then((result: RxDocument<T>) => {
              const proxiedResult = createUpdateProxy(result, setData);
              setData(proxiedResult);
              return proxiedResult;
            });
        };
      }
      return originalValue;
    },
  });
}
